import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { runtimeEnvironment } from '../config/environment';
import { getSiteGateStatus, unlockSiteGate } from '../services/siteGateApiService';
import { siteGateManager } from '../services/siteGateManager';

interface SiteGateContextType {
    /** Whether the gate applies to this environment at all. */
    required: boolean;
    /** Whether this browser has satisfied the gate. */
    unlocked: boolean;
    /** True until the initial status check resolves. */
    isLoading: boolean;
    /** Submit the shared password; on success flips `unlocked` to true. Returns success. */
    unlock: (password: string) => Promise<boolean>;
}

const SiteGateContext = createContext<SiteGateContextType | undefined>(undefined);

/** The gate is only ever relevant in the deployed lower environments. */
const GATED_ENVIRONMENTS = ['test', 'cert'];
const isGatedEnvironment = GATED_ENVIRONMENTS.includes(runtimeEnvironment);

/**
 * Fetches sitewide-gate status once on load (only in test/cert) and exposes an `unlock` action.
 * In prod/local it is inert and never touches the network.
 */
export const SiteGateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [required, setRequired] = useState<boolean>(false);
    const [unlocked, setUnlocked] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(isGatedEnvironment);

    useEffect(() => {
        if (!isGatedEnvironment) {
            return; // Inert outside test/cert.
        }

        let isMounted = true;
        getSiteGateStatus()
            .then(status => {
                if (!isMounted) return;
                setRequired(status.required);
                setUnlocked(status.unlocked);
            })
            .catch(() => {
                // Fail open: don't lock developers out on a network error.
                if (!isMounted) return;
                setRequired(false);
                setUnlocked(true);
            })
            .finally(() => {
                if (isMounted) setIsLoading(false);
            });

        // Re-lock if any API call returns 511 mid-session (expired/cleared cookie).
        const unsubscribe = siteGateManager.subscribe(() => {
            if (isMounted) {
                setRequired(true);
                setUnlocked(false);
            }
        });

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, []);

    const unlock = useCallback(async (password: string): Promise<boolean> => {
        const success = await unlockSiteGate(password);
        if (success) {
            setUnlocked(true);
        }
        return success;
    }, []);

    return (
        <SiteGateContext.Provider value={{ required, unlocked, isLoading, unlock }}>
            {children}
        </SiteGateContext.Provider>
    );
};

/** Hook to access the sitewide-gate state. */
export const useSiteGate = (): SiteGateContextType => {
    const context = useContext(SiteGateContext);
    if (context === undefined) {
        throw new Error('useSiteGate must be used within a SiteGateProvider');
    }
    return context;
};
